import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Star, Plus, Trash2, Pencil, X, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ author: "", rating: 5, text: "", business: "", date: "" });

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch {}
  };

  useEffect(() => { fetchReviews(); }, []);

  const resetForm = () => {
    setForm({ author: "", rating: 5, text: "", business: "", date: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author || !form.text) { toast.error("Remplissez au moins le nom et l'avis"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/reviews/${editingId}`, form);
        toast.success("Avis modifié");
      } else {
        await axios.post(`${API}/admin/reviews`, form);
        toast.success("Avis ajouté");
      }
      resetForm();
      fetchReviews();
    } catch { toast.error("Erreur"); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`);
      toast.success("Avis supprimé");
      fetchReviews();
    } catch { toast.error("Erreur"); }
  };

  const editReview = (r) => {
    setForm({ author: r.author, rating: r.rating, text: r.text, business: r.business || "", date: r.date || "" });
    setEditingId(r.id);
    setShowForm(true);
  };

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "0";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-5 text-center">
          <p className="text-[#666] text-xs uppercase tracking-wider mb-1">Total avis</p>
          <p className="text-white font-bold text-3xl">{reviews.length}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-5 text-center">
          <p className="text-[#666] text-xs uppercase tracking-wider mb-1">Note moyenne</p>
          <p className="text-[#EFFF00] font-bold text-3xl">{avgRating}/5</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-5 text-center">
          <p className="text-[#666] text-xs uppercase tracking-wider mb-1">5 étoiles</p>
          <p className="text-green-400 font-bold text-3xl">{reviews.filter(r => r.rating === 5).length}</p>
        </div>
      </div>

      <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#EFFF00]" />
            Avis Google
          </h2>
          <Button
            onClick={() => { resetForm(); setShowForm(!showForm); }}
            className={showForm ? "bg-[#262626] text-white" : "bg-[#EFFF00] text-black hover:bg-[#d4e000]"}
            data-testid="add-review-btn"
          >
            {showForm ? <><X className="w-4 h-4 mr-2" />Annuler</> : <><Plus className="w-4 h-4 mr-2" />Ajouter un avis</>}
          </Button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-[#262626] rounded-xl p-5 mb-6 space-y-4" data-testid="review-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#A3A3A3] mb-1">Nom du client *</label>
                <Input value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} placeholder="Marie D."
                  className="bg-[#1a1a1a] border-[#262626] text-white" data-testid="review-author" required />
              </div>
              <div>
                <label className="block text-sm text-[#A3A3A3] mb-1">Commerce</label>
                <Input value={form.business} onChange={(e) => setForm({...form, business: e.target.value})} placeholder="Boulangerie Le Pain Doré"
                  className="bg-[#1a1a1a] border-[#262626] text-white" data-testid="review-business" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#A3A3A3] mb-1">Note *</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setForm({...form, rating: n})}
                      className="p-1 transition-transform hover:scale-110" data-testid={`review-star-${n}`}>
                      <Star className={`w-7 h-7 ${n <= form.rating ? 'fill-[#EFFF00] text-[#EFFF00]' : 'text-[#333]'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#A3A3A3] mb-1">Date (optionnel)</label>
                <Input value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} placeholder="15/03/2026"
                  className="bg-[#1a1a1a] border-[#262626] text-white" data-testid="review-date" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#A3A3A3] mb-1">Avis *</label>
              <textarea value={form.text} onChange={(e) => setForm({...form, text: e.target.value})} rows={3}
                placeholder="Excellente prestation ! L'enseigne a transformé ma vitrine..."
                className="w-full bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 text-white resize-none text-sm" data-testid="review-text" required />
            </div>
            <Button type="submit" className="bg-[#EFFF00] text-black hover:bg-[#d4e000] font-bold" data-testid="review-submit-btn">
              {editingId ? "Modifier l'avis" : "Ajouter l'avis"}
            </Button>
          </form>
        )}

        {reviews.length === 0 ? (
          <div className="text-center py-10">
            <MessageSquare className="w-12 h-12 text-[#333] mx-auto mb-3" />
            <p className="text-[#666]">Aucun avis pour le moment</p>
            <p className="text-[#444] text-sm mt-1">Ajoutez vos avis Google pour qu'ils s'affichent sur la landing page</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-full bg-[#EFFF00]/20 flex items-center justify-center text-[#EFFF00] font-bold text-sm">
                        {r.author[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{r.author}</p>
                        {r.business && <p className="text-[#666] text-xs">{r.business}</p>}
                      </div>
                      <div className="flex gap-0.5 ml-2">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'fill-[#EFFF00] text-[#EFFF00]' : 'text-[#333]'}`} />
                        ))}
                      </div>
                      {r.date && <span className="text-[#444] text-xs ml-auto">{r.date}</span>}
                    </div>
                    <p className="text-[#A3A3A3] text-sm leading-relaxed">"{r.text}"</p>
                  </div>
                  <div className="flex gap-1 ml-4">
                    <button onClick={() => editReview(r)} className="p-2 text-[#666] hover:text-[#EFFF00] transition-colors" data-testid={`edit-review-${r.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteReview(r.id)} className="p-2 text-[#666] hover:text-red-400 transition-colors" data-testid={`delete-review-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
        <h3 className="text-sm font-bold text-[#A3A3A3] uppercase tracking-wider mb-3">Comment ça marche</h3>
        <div className="space-y-2 text-[#666] text-sm">
          <p>1. Ajoutez vos vrais avis Google ici (copiez-les depuis votre fiche Google My Business)</p>
          <p>2. Les avis s'affichent automatiquement dans la section "Avis Clients" de la landing page</p>
          <p>3. Les avis avec 5 étoiles sont affichés en priorité pour maximiser la conversion</p>
        </div>
      </div>
    </div>
  );
}
